import { action, computed, intercept, makeObservable, observable } from 'mobx'
import { IValidationSchema, required, validationContext } from 'validx'
import { Id } from 'types/definitions'
import { maxLength, mustBeValidTaxFormInputValue } from 'utils/validation-utils'
import PersonalInfoEditorViewModel from './PersonalInfoEditorViewModel'
import { DependentProfileVersion } from '../DependentProfile/DependentProfileVersion'

interface DependentBaseline {
  firstName: string
  middleInitial: string
  lastName: string
  dateOfBirth: string
  personalTaxpayerIdentificationNumber: string
  relationship: string
}

export default class DependentProfileEditorViewModel {
  @observable id: Id = undefined

  @observable version = 0

  readonly personalInfoVm = new PersonalInfoEditorViewModel()

  @observable relationship = ''

  @observable.ref private baseline: DependentBaseline | null = null

  private schema: IValidationSchema<this> = {
    relationship: [
      required('Relationship is required'),
      mustBeValidTaxFormInputValue(),
      maxLength(100, 'Relationship must not exceed 100 characters'),
    ],
  }

  validation = validationContext(this)

  constructor() {
    makeObservable(this)

    intercept(this, (change) => {
      this.validation.clearErrors(String(change.name))
      return change
    })
  }

  @action.bound
  validate() {
    const ownValid = this.validation.reset().validate(this.schema).isValid
    const personalValid = this.personalInfoVm.validate()
    return ownValid && personalValid
  }

  @computed
  get isDirty() {
    const baseline = this.baseline
    if (!baseline) {
      return true
    }
    return (
      this.personalInfoVm.firstName !== baseline.firstName ||
      this.personalInfoVm.middleInitial !== baseline.middleInitial ||
      this.personalInfoVm.lastName !== baseline.lastName ||
      this.personalInfoVm.dateOfBirth !== baseline.dateOfBirth ||
      this.personalInfoVm.personalTaxpayerIdentificationNumber !==
        baseline.personalTaxpayerIdentificationNumber ||
      this.relationship !== baseline.relationship
    )
  }

  @action.bound
  reset(profile?: DependentProfileVersion | null) {
    this.id = profile?.id
    this.version = profile?.version ?? 0
    const personalInfo = profile?.personalInfo
    this.personalInfoVm.reset(
      personalInfo
        ? {
            firstName: personalInfo.firstName,
            middleInitial: personalInfo.middleInitial,
            lastName: personalInfo.lastName,
            dateOfBirth: personalInfo.dateOfBirth,
            personalTaxpayerIdentificationNumber:
              personalInfo.personalTaxpayerIdentificationNumber,
          }
        : undefined,
    )
    this.relationship = profile?.relationship ?? ''
    this.baseline = profile
      ? {
          firstName: this.personalInfoVm.firstName,
          middleInitial: this.personalInfoVm.middleInitial,
          lastName: this.personalInfoVm.lastName,
          dateOfBirth: this.personalInfoVm.dateOfBirth,
          personalTaxpayerIdentificationNumber:
            this.personalInfoVm.personalTaxpayerIdentificationNumber,
          relationship: this.relationship,
        }
      : null
    this.validation.reset()
  }
}
